<script setup>

import { theme } from '#tailwind-config'

const route = useRoute()

const screensWidth = ref({ ... theme.screens })
const gridColumnsCount = ref(null)
const groupCovers = ref(null)
const changedLimit = ref(null)

const sizerCookie = useCookie('sizerCookie')
const sizerValue = ref(sizerCookie.value || null)

const isLicense = route.name.includes('license___')

const columnStyle = ref({
	transform: 'translateY(10%)',
	opacity: 0,
})

const props = defineProps({
  covers: {
    type: Array,
  },
	cursor: {
		type: String || null
	},
	loading: {
		type: Boolean,
		default: false
	},
	noPadding: {
		type: Boolean,
		default: false
	},
	smallGrid: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['loadMore', 'changeLimit'])

const { $listen } = useNuxtApp()

$listen('setSizerValue', (value) => {
	gridColumnsCount.value = parseInt(value)
})



onMounted(() => {
  window.addEventListener('resize', onResize)
	onResize()

	groupCovers.value = chunkIntoN([...props.covers], gridColumnsCount.value)

	setTimeout(() => {
		columnStyle.value = {
			transform: 'translateY(0)',
			opacity: 1,
		}
	}, 200)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

const onResize = () => {
  // set columns count
	if (window.innerWidth < parseInt(screensWidth.value['2xl'].replace('px', '')) || !sizerValue.value) {
		if (window.innerWidth >= parseInt(screensWidth.value.fullHD.replace('px', ''))) {
			gridColumnsCount.value = 4 
		} else if (window.innerWidth >= parseInt(screensWidth.value.lg.replace('px', ''))) {
			gridColumnsCount.value = 3
		} else if (window.innerWidth >= parseInt(screensWidth.value.md.replace('px', ''))) {
			gridColumnsCount.value = 2
		} else {
			gridColumnsCount.value = 1
		}
	} else {
		if (isLicense) {
			gridColumnsCount.value = 3
		} else {
			gridColumnsCount.value = parseInt(sizerValue.value) || 4
		}
	}
}

const chunkIntoN = (array, n) => {
	
  let result = [];
	for (let i = n; i > 0; i--) {
		result.push([])
	}

	let arrayChunkIndex = 0

	array.forEach(element => {
		result[arrayChunkIndex].push(element)
		
		if (arrayChunkIndex === n - 1) {
			arrayChunkIndex = 0
		} else {
			arrayChunkIndex++
		}
	});
	
	return result;
}

watch(gridColumnsCount, () => {
  groupCovers.value = chunkIntoN([...props.covers], gridColumnsCount.value)
})

watch(sizerValue, () => {
  if(sizerValue?.value) {
		gridColumnsCount.value = parseInt(sizerValue.value)
	}
})

watch(() => props.covers, () => {
 groupCovers.value = chunkIntoN([...props.covers], gridColumnsCount.value)
});

</script>

<template>
	<div class="relative">
		<div v-if="loading" class="absolute w-full top-24 left-0 right-0 mx-auto z-10">
			<Loader />
		</div>
		<div :class="[noPadding ? 'py-0' : 'container py-8 xl:py-20']">
			<div v-if="covers.length" :class="`grid grid-cols-${gridColumnsCount} gap-[30px]`">
				<div
					v-for="column in gridColumnsCount"
					:key="column"
					class="grid-column"
					:class="`grid-column-${column}`"
					:data-column="`c${column}`"
					:style="{
						opacity: columnStyle.opacity,
						transform: columnStyle.transform,
					}"
				>
					<div class="mb-8" v-for="(cover, index) in groupCovers[column - 1]" :key="`i${index}-${cover.id}`">
						<CoverArtist v-if="cover.type === 'Artist'" :attrs="cover.attrs" :id="cover.id" />
						<CoverTrack v-else-if="cover.type === 'Track'" :attrs="cover.attrs" :id="cover.id" />
						<CoverAlbum v-else-if="cover.type === 'Album'" :attrs="cover.attrs" :id="cover.id" />
						<CoverArticle v-else-if="cover.type === 'Article' || cover.type === 'Podcast' || cover.type === 'Release'" :attrs="cover.attrs" :id="cover.id" :type="cover.type.toLowerCase()" />
						<CoverFact v-else-if="cover.type === 'Fact'" :attrs="cover.attrs" :id="cover.id" />
						<CoverNewsletter v-else-if="cover.type === 'Newsletter'" :attrs="cover.attrs" :id="cover.id" />
						<CoverDefault v-else :attrs="cover.attrs" :id="cover.id" :type="cover.type" />
					</div>
				</div>
			</div>

			<div v-else class="h5 dark:text-light">
				{{ $t('generic.items-not-found') }}
			</div>

			<LoadMore @click="emit('loadMore')" v-if="cursor" :loading="loading" smallGrid />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.grid-column {
	transition: 1s cubic-bezier(0.390, 0.575, 0.565, 1.000);

	&-1 {
		transition-delay: 0;
	}
	&-2 {
		transition-delay: 150ms;
	}
	&-3 {
		transition-delay: 300ms;
	}
	&-4 {
		transition-delay: 450ms;
	}
}
</style>